/**
 * @module Manager
 */

import { ApmBase } from "@elastic/apm-rum";
import { Viewer as InternalViewer } from "@inovitas/infra3dsdk";
import jwtDecode from "jwt-decode";

import {
  getProviderType,
  getURL,
} from "shared/common/provider/userProviderDecision";
import { clientConfig } from "shared/common/stores/defaultStore";
import { monitor, setApmUserContext } from "./apm-logging";
import { EventEmitter } from "./EventEmitter";
import { getUserData, tokenType } from "./Login";
import { Viewer } from "./Viewer";
import {
  getApiKeyHeaders,
  getAuthorizationHeaders,
} from "shared/common/utils/general-utils";

/**
 * Represents properties of an application.
 */
type applicationProps = {
  name: string;
  url: string;
};

/**
 * Represents properties of a project card.
 */
type cardProps = {
  uid: string;
  name: string;
  description: string;
  applications: applicationProps[];
  created_by: string;
  creation_date: string | Date;
  tags: Array<{ name: string; font_color: string; background_color: string }>;
};

export type initViewerOptions = {
  /**
   * The unique identifier for the project.
   */
  project_uid: string;
  /**
   * An optional object containing the start position with easting and northing coordinates and an optional epsg.
   */
  start_position?: {
    /**
     * The easting coordinate for the start position.
     */
    easting: number;
    /**
     * The northing coordinate for the start position.
     */
    northing: number;
    /**
     * The epsg-code for the given easting / northing. Default is 4326 - WGS84.
     */
    epsg?: number;
  };
  /**
   * An optional object containing the start campaign. Default is the first entry.
   */
  start_campaign?: { id: string };
  /**
   * An optional boolean flag indicating whether to expand the map. Default is false.
   */
  map_expand?: boolean;
  /**
   * An optional boolean flag indicating whether to show the cockpit. Default is true.
   */
  show_cockpit?: boolean;
  /**
   * An optional boolean flag indicating whether to show a map window. Default is true.
   */
  show_mapWindow?: boolean;
  /**
   * An optional boolean flag indicating whether to show a toolbar. Default is false.
   */
  show_toolbar?: boolean;
  /**
   * An optional boolean flag indicating whether to show a topbar. Default is true.
   */
  show_topbar?: boolean;
  /**
   * @deprecated toolbar is not used anymore, as it is derived from the access token.
   */
  toolbar?: string;
};

/**
 * Manager Class
 *
 * The Manager class extends the EventEmitter class.
 * It serves as a central component for managing the infra3D application.
 *
 * @extends EventEmitter - Inherits functionality for event handling.
 */
export class Manager extends EventEmitter {
  /**
   * Static Properties:
   * - viewinitialized: Represents an event emitted when a view is initialized.
   * - viewerset: Represents an event emitted when a viewer is set.
   * - tokenset: Represents an event emitted when tokens are set.
   */
  public static viewinitialized = "viewinitialized";
  public static viewerset = "viewerset";
  public static tokenset = "tokenset";

  private _apm: ApmBase;
  private _tokens: any;
  private _viewer: Viewer | null = null;

  /**
   * @ignore
   */
  constructor(apm: ApmBase) {
    super();
    this._apm = apm;
    this._tokens = {};
  }

  /**
   * Initialize Viewer
   *
   * Initializes the viewer with the given project UID and optional options.
   *
   * @param {Object} options - An object containing the initialization options.
   * @returns {Promise<Viewer>} A Promise that resolves with a Viewer instance.
   */
  public initViewer(options: initViewerOptions): Promise<Viewer> {
    if (options.toolbar) {
      console.warn(
        "The toolbar option is deprecated. It is instead parsed from the access token."
      );
    }
    monitor(this._apm, "manager.initViewer");
    this.fire(Manager.viewinitialized, options);
    return new Promise((resolve) => {
      this.on(Manager.viewerset, (viewer) => {
        resolve(viewer);
      });
    });
  }

  /**
   * Get Projects
   *
   * Fetches project data, such as project information.
   *
   * @returns {Promise<cardProps[]>} A Promise that resolves with an array of cardProps representing project cards.
   */
  public async getProjects(): Promise<cardProps[]> {
    const userData = getUserData(this._tokens);
    const providerType = getProviderType(userData.userpool_id);
    const url = getURL(
      userData.tenant,
      userData.usergroups,
      userData.username,
      userData.userpool_id,
      "?fields=uid,name,description,applications,tags,creation_date,created_by",
      providerType
    );
    monitor(this._apm, "manager.getProjects", undefined, {
      username: userData.username,
      tenant: userData.tenant,
      usergroup: userData.usergroups.join(","),
    });

    try {
      const response = await fetch(url, {
        headers: {
          ...getAuthorizationHeaders(this._tokens.access_token),
          ...getApiKeyHeaders(),
        },
      });
      if (!response.ok) {
        throw new Error("Error fetching projects");
      }
      const data = await response.json();
      data.value.sort((a: any, b: any) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      return data.value;
    } catch {
      throw new Error("Error fetching projects");
    }
  }

  /**
   * Set Viewer
   *
   * Sets a viewer instance.
   *
   * @param {InternalViewer} viewer - The Viewer instance to set.
   */
  public setViewer(viewer: InternalViewer, clientConfig: clientConfig) {
    this._viewer = new Viewer(viewer, clientConfig);
    this.fire(Manager.viewerset, this._viewer);
  }

  public setTokens(tokens: tokenType) {
    this._tokens = tokens;
    const decoded_token: any = jwtDecode(tokens.access_token);
    const scopes = decoded_token.scope.split(" ");
    if (!scopes.includes("addon/api")) {
      throw new Error("No api permission in token");
    }
    this._apm.addLabels({
      sessionid: decoded_token.jti,
    });
    this.fire(Manager.tokenset, tokens);
  }

  public setApmUserContext(userContext?: {
    id?: string;
    username?: string;
    email?: string;
  }) {
    setApmUserContext(this._apm, this._tokens, userContext);
  }

  public getViewer() {
    return this._viewer;
  }
}
